"use client";

import { Button, Field, Stack, Text, VStack } from "@chakra-ui/react";
import type { Version } from "@/generated/fastapi";
import GenericConfirmationDialog from "@/shared/ui/GenericConfirmationDialog";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";
import useConfirmationDialog from "@/shared/ui/useConfirmationDialog";
import VersionSelector from "./VersionSelector";
import type { VersionSelectorState } from "./types";

type LoadVersionSectionProps = VersionSelectorState & {
  canApplyVersion: boolean;
  canDelete: boolean;
  deletePending: boolean;
  onDeleteVersion: () => Promise<void>;
  onLoadVersion: () => void;
  selectedVersion?: Version;
};

export default function LoadVersionSection({
  canApplyVersion,
  canDelete,
  deletePending,
  onDeleteVersion,
  onLoadVersion,
  selectedVersion,
  selectedVersions,
  setSelectedVersions,
  selectorEpoch,
  versionItems,
  versions,
}: LoadVersionSectionProps) {
  const confirmVersionDeletion = useConfirmationDialog();

  const handleDeleteClick = () => {
    confirmVersionDeletion.onOpen(onDeleteVersion);
  };

  return (
    <VStack align="stretch" gap="4">
      <GenericDrawerInfo
        description="Load a profile version into Studio. This replaces the current temporary profile selection."
        items={[
          { label: "Load", colorPalette: "blue" },
          {
            label: selectedVersion ? selectedVersion.name : "No version selected",
            colorPalette: selectedVersion ? "green" : "gray",
          },
          {
            label: `${versions.length} saved`,
            colorPalette: versions.length > 0 ? "teal" : "gray",
          },
        ]}
      />

      {versions.length === 0 ? (
        <GenericEmptyComponent message="No saved profile versions yet" />
      ) : (
        <>
          <Field.Root>
            <Field.Label>Saved version</Field.Label>
            <VersionSelector
              selectedVersions={selectedVersions}
              setSelectedVersions={setSelectedVersions}
              selectorEpoch={selectorEpoch}
              versionItems={versionItems}
              versions={versions}
              placeholder="Choose a version to load"
            />
            <Field.HelperText>
              Loading a version replaces the current temporary profile selection.
            </Field.HelperText>
          </Field.Root>

          <Stack
            direction="row"
            justify="space-between"
            align="center"
            gap="3"
            pt="2"
          >
            <WithLongPress
              onLongPress={handleDeleteClick}
              enabled={canDelete && !deletePending}
              threshold="critical"
              longPressHint="Hold to delete version"
            >
              <Button
                variant="ghost"
                colorPalette="red"
                loading={deletePending}
                disabled={!canDelete}
                aria-label="Delete version"
                title="Hold to delete version"
                minW={{ base: "10", sm: "auto" }}
              >
                <GenericIcon icon="delete_permanent" />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  Delete version
                </Text>
              </Button>
            </WithLongPress>
            <Button
              colorPalette="blue"
              disabled={!canApplyVersion}
              onClick={onLoadVersion}
              aria-label="Load into Studio"
              title="Load into Studio"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="load" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Load into Studio
              </Text>
            </Button>
          </Stack>
          <GenericConfirmationDialog
            isOpen={confirmVersionDeletion.isOpen}
            onCloseAction={confirmVersionDeletion.onClose}
            onConfirmAction={confirmVersionDeletion.onConfirm}
            title="Delete Profile Version"
            description={`Delete ${
              selectedVersions.length === 1
                ? selectedVersions[0]?.name ?? "this saved profile version"
                : `${selectedVersions.length} saved profile versions`
            }? This cannot be undone.`}
            confirmText="Delete"
          />
        </>
      )}
    </VStack>
  );
}
